<script setup lang="ts">
import { computed } from "vue"
import { XMarkIcon } from "@heroicons/vue/24/outline"
import { useOverlay } from "@/composables/useOverlay"

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full"

const props = withDefaults(
  defineProps<{
    title?: string
    size?: ModalSize
    closeOnClickOutside?: boolean
    closeOnEscape?: boolean
    showCloseButton?: boolean
    showOverlay?: boolean
    modalClass?: string
    overlayClass?: string
    persistent?: boolean
    zIndex?: number
    noPadding?: boolean
  }>(),
  {
    size: "md",
    closeOnClickOutside: true,
    closeOnEscape: true,
    showCloseButton: true,
    showOverlay: true,
    persistent: false,
    zIndex: 50,
    noPadding: false,
  }
)

const emit = defineEmits<{
  onClose: [value: boolean]
  "before-open": []
  opened: []
  "before-close": []
  closed: []
}>()

const modelValue = defineModel<boolean>({ required: true })

const { overlayRef, contentRef, handleClickOutside } = useOverlay(modelValue, {
  closeOnEscape: props.persistent ? false : props.closeOnEscape,
  closeOnClickOutside: props.persistent ? false : props.closeOnClickOutside,
  onClose: () => {
    if (!props.persistent) {
      modelValue.value = false
      emit("onClose", false)
    }
  },
})

const sizeClasses = computed(() => {
  const sizes: Record<ModalSize, string> = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-7xl",
  }
  return sizes[props.size ?? "md"]
})

const zIndexClass = computed(() => {
  const zIndexMap: Record<number, string> = {
    10: "z-10",
    20: "z-20",
    30: "z-30",
    40: "z-40",
    50: "z-50",
    60: "z-60",
  }
  return zIndexMap[props.zIndex!] || "z-50"
})

const handleClose = () => {
  if (!props.persistent) {
    modelValue.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-overlay">
      <div
        v-if="modelValue && showOverlay"
        :class="['fixed inset-0 modal-overlay-bg', zIndexClass, overlayClass]"
        @click="handleClickOutside"
      />
    </Transition>

    <Transition name="modal-content">
      <div
        v-if="modelValue"
        ref="overlayRef"
        :class="['fixed inset-0 flex items-center justify-center p-4 modal-container', zIndexClass]"
      >
        <div
          ref="contentRef"
          :class="['relative bg-white rounded-lg shadow-xl w-full', sizeClasses, modalClass]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? 'modal-title' : undefined"
          @click.stop
        >
          <div
            v-if="title || $slots.header || showCloseButton"
            class="flex items-start justify-between p-5 border-b border-slate-200"
          >
            <div class="flex-1">
              <slot name="header">
                <h3 v-if="title" id="modal-title" class="text-lg font-semibold text-slate-900">
                  {{ title }}
                </h3>
              </slot>
            </div>

            <button
              v-if="showCloseButton"
              type="button"
              class="ml-auto -m-2 p-2 text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400 rounded-lg transition-colors"
              aria-label="Fechar"
              @click="handleClose"
            >
              <XMarkIcon class="w-5 h-5" />
            </button>
          </div>

          <div :class="noPadding ? 'flex-1 flex flex-col' : 'p-6'">
            <slot />
          </div>

          <div
            v-if="$slots.footer"
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.modal-overlay-bg {
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.modal-container {
  background: transparent !important;
}

.modal-overlay-enter-active,
.modal-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.modal-overlay-enter-from,
.modal-overlay-leave-to {
  opacity: 0;
}

.modal-content-enter-active {
  transition: all 0.3s ease;
}

.modal-content-leave-active {
  transition: all 0.2s ease;
}

.modal-content-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
